import { Link } from 'react-router-dom';
import { countries } from '@/mocks/countries';
import { useState } from 'react';

const features = [
  {
    icon: 'ri-home-office-line',
    title: 'Apply From Home',
    description: 'Complete your entire visa application from the comfort of your home or office. No need to visit our office unless required.',
  },
  {
    icon: 'ri-file-search-line',
    title: 'Document Review',
    description: 'Our experts carefully review all your supporting documents to ensure they meet embassy requirements before submission.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Appointment Booking',
    description: 'We handle embassy and visa centre appointment scheduling, saving you time and ensuring you get the earliest available slots.',
  },
  {
    icon: 'ri-truck-line',
    title: 'Document Collection',
    description: 'Our team collects your documents and passport from your preferred location and delivers them back after processing.',
  },
  {
    icon: 'ri-timer-line',
    title: 'Real-Time Tracking',
    description: 'Track your application status online using your unique PVS reference number at every stage of the process.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Premium Support',
    description: 'Dedicated visa consultants provide personalised guidance throughout your application journey from start to finish.',
  },
];

const steps = [
  { number: '01', title: 'Apply Online', description: 'Fill out our simple online application form from home with your travel details and upload your documents.' },
  { number: '02', title: 'Document Review', description: 'Our visa specialists review your documents for completeness and compliance with embassy requirements.' },
  { number: '03', title: 'Appointment Booking', description: 'We schedule your appointment at the relevant embassy or visa application centre.' },
  { number: '04', title: 'Submit & Track', description: 'Your application is submitted and you can track progress online using your PVS reference number.' },
];

export default function Home() {
  const [countryFilter, setCountryFilter] = useState('All');

  const regions = ['All', 'Africa', 'Middle East', 'Asia', 'Europe/Asia'];
  const filteredCountries = countryFilter === 'All'
    ? countries
    : countries.filter((c) => c.region === countryFilter);

  // Hide France from the homepage country listing only.
  const displayedCountries = filteredCountries.filter((c) => c.code !== 'FR');

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: 'url("https://storage.readdy-site.link/project_files/15f4e7dd-e78a-49fd-9a05-a56342ae6a80/b1a33608-96ba-4856-9fa8-024799e1ab7d_compressed_ChatGPT-Image-Jul-17-2026-12_19_41-PM.webp")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 via-primary-950/50 to-primary-950/35"></div>
        <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-accent-500/90 text-background-50 px-4 py-1.5 rounded-full text-xs md:text-sm font-medium mb-6">
            <i className="ri-map-pin-line"></i>
            <span>Based in Lusaka, Zambia</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-5 leading-tight">
            Premium Visa Solutions Comes to Zambia
          </h1>
          <p className="text-sm md:text-lg text-background-50/85 mb-8 max-w-[700px] mx-auto leading-relaxed">
            Apply for your visa from home with professional visa support, secure document handling, appointment assistance, and real-time application tracking.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/apply-now"
              className="w-full sm:w-auto px-8 md:px-10 py-3 md:py-3.5 bg-accent-500 text-background-50 font-semibold rounded-md hover:bg-accent-600 transition-all duration-200 text-sm md:text-base whitespace-nowrap cursor-pointer"
            >
              Apply for Visa Now
            </Link>
            <Link
              to="/apply-from-home"
              className="w-full sm:w-auto px-8 md:px-10 py-3 md:py-3.5 bg-background-50/15 text-background-50 font-semibold rounded-md border border-background-50/30 hover:bg-background-50/25 transition-all duration-200 text-sm md:text-base whitespace-nowrap cursor-pointer"
            >
              Apply From Home
            </Link>
            <Link
              to="/track-application"
              className="w-full sm:w-auto px-8 md:px-10 py-3 md:py-3.5 bg-background-50 text-primary-800 font-semibold rounded-md hover:bg-secondary-100 transition-all duration-200 text-sm md:text-base whitespace-nowrap cursor-pointer"
            >
              Track Application
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">Why Choose Us</span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              Professional Visa Support You Can Trust
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-[600px] mx-auto">
              We make the visa application process simple, transparent, and stress-free with our comprehensive services.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {features.map((feature) => (
              <div key={feature.title} className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6 hover:border-accent-300 transition-colors duration-200 group cursor-pointer">
                <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center bg-accent-100 rounded-lg mb-4 group-hover:bg-accent-500 transition-colors duration-200">
                  <i className={`${feature.icon} text-accent-600 group-hover:text-background-50 text-lg md:text-xl transition-colors duration-200`}></i>
                </div>
                <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-14 md:py-20 bg-secondary-100">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <span className="inline-block bg-primary-100 text-primary-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">Simple Process</span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              How It Works
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-[600px] mx-auto">
              Four simple steps to get your visa application started with Premium Visa Solutions.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
            {steps.map((step, idx) => (
              <div key={step.number} className="relative">
                <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center bg-primary-500 text-background-50 rounded-full font-heading font-bold text-lg mb-4">
                    {step.number}
                  </div>
                  <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.description}</p>
                </div>
                {idx < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8">
                    <div className="w-5 h-5 flex items-center justify-center text-accent-500">
                      <i className="ri-arrow-right-s-line text-xl"></i>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-10">
            <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">Global Reach</span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              Visas Available for the Following Countries
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-[650px] mx-auto">
              We provide visa application support for over 30 destinations across Africa, the Middle East, Asia, and beyond.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
            {regions.map((region) => (
              <button
                key={region}
                onClick={() => setCountryFilter(region)}
                className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  countryFilter === region
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-secondary-100 text-foreground-600 hover:bg-secondary-200'
                }`}
              >
                {region}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
            {displayedCountries.map((country) => (
              <div
                key={country.code}
                className="bg-background-50 border border-secondary-200 rounded-lg p-4 md:p-5 text-center hover:border-primary-300 transition-all duration-200 cursor-pointer group"
              >
                <div className="w-10 h-7 mx-auto mb-2.5 overflow-hidden rounded-sm">
                  <img
                    src={`https://flagcdn.com/w160/${country.code.toLowerCase()}.png`}
                    alt={`Flag of ${country.name}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <p className="text-sm md:text-base font-medium text-primary-800 group-hover:text-accent-600 transition-colors duration-200">{country.name}</p>
                <p className="text-xs text-foreground-500 mt-0.5">{country.region}</p>
              </div>
            ))}
          </div>

          {displayedCountries.length === 0 && (
            <p className="text-center text-foreground-500 text-sm mt-8">No countries found for this region.</p>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20dark%20navy%20blue%20abstract%20background%20with%20subtle%20geometric%20world%20map%20lines%20and%20warm%20orange%20accent%20glow%2C%20premium%20corporate%20style%2C%20clean%20modern%20design%20with%20travel%20theme%2C%20no%20text&width=1920&height=600&seq=cta-visa-apply-2026&orientation=landscape"
          alt="Ready to start your visa application"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="Start your visa application today - Premium Visa Solutions"
        />
        <div className="absolute inset-0 bg-primary-900/85"></div>
        <div className="relative z-10 w-full max-w-[800px] mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
            Ready to Start Your Visa Application?
          </h2>
          <p className="text-sm md:text-base text-background-50/80 mb-8 max-w-[600px] mx-auto">
            Let our experienced team handle your visa application while you focus on planning your trip. Apply from the comfort of your home today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/apply-now"
              className="w-full sm:w-auto px-10 py-3.5 bg-accent-500 text-background-50 font-semibold rounded-md hover:bg-accent-600 transition-all duration-200 text-sm md:text-base whitespace-nowrap cursor-pointer"
            >
              Apply Now
            </Link>
            <Link
              to="/apply-from-home"
              className="w-full sm:w-auto px-10 py-3.5 bg-background-50/10 text-background-50 font-semibold rounded-md border border-background-50/30 hover:bg-background-50/20 transition-all duration-200 text-sm md:text-base whitespace-nowrap cursor-pointer"
            >
              Apply From Home
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-16 bg-background-50 border-y border-secondary-200">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center bg-accent-100 rounded-full mb-3">
                <i className="ri-user-star-line text-accent-600 text-xl md:text-2xl"></i>
              </div>
              <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">5,000+</p>
              <p className="text-sm text-foreground-600 mt-1">Applications Processed</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center bg-primary-100 rounded-full mb-3">
                <i className="ri-global-line text-primary-600 text-xl md:text-2xl"></i>
              </div>
              <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">30+</p>
              <p className="text-sm text-foreground-600 mt-1">Countries Covered</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center bg-accent-100 rounded-full mb-3">
                <i className="ri-team-line text-accent-600 text-xl md:text-2xl"></i>
              </div>
              <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">15+</p>
              <p className="text-sm text-foreground-600 mt-1">Expert Consultants</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center bg-primary-100 rounded-full mb-3">
                <i className="ri-thumb-up-line text-primary-600 text-xl md:text-2xl"></i>
              </div>
              <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">98%</p>
              <p className="text-sm text-foreground-600 mt-1">Client Satisfaction</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}